'use client';

import * as RadixPopover from '@radix-ui/react-popover';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';

import { returnRefAndGenerateDataTracker } from '@/decodo/components/atoms/Button/utils';

import { HTMLDataTrackerProps } from '@/app/types';

export const positionOptions = ['top', 'right', 'bottom', 'left'] as const;

export interface PopoverProps {
  target: React.ReactNode;
  children: React.ReactNode;
  showArrow?: boolean;
  onChange?: (value: boolean) => void;
  onChangeCallback?: (value: boolean) => void;
  isOpen?: boolean;
  contentProps?: RadixPopover.PopoverContentProps;
  targetProps?: RadixPopover.PopoverTriggerProps & HTMLDataTrackerProps;
  dynamicWidth?: boolean;
  classNames?: {
    target?: string;
    childrenWrapper?: string;
    arrow?: string;
    content?: string;
  };
  position?: (typeof positionOptions)[number];
}

function Popover({
  target,
  children,
  showArrow = true,
  isOpen,
  classNames,
  dynamicWidth,
  onChange,
  onChangeCallback,
  position = 'bottom',
  contentProps,
  targetProps,
}: PopoverProps) {
  const [isOpenInner, setIsOpenInner] = useState(isOpen ?? false);

  const currentIsOpen = isOpen ?? isOpenInner;

  function togglePopover() {
    handleChange(!currentIsOpen);
  }

  function handleChange(value: boolean) {
    if (onChange) {
      onChange(value);
    } else {
      setIsOpenInner(value);
    }

    if (onChangeCallback) onChangeCallback(value);
  }

  return (
    <RadixPopover.Root onOpenChange={togglePopover} open={currentIsOpen}>
      <RadixPopover.Trigger
        {...targetProps}
        ref={(_ref) => {
          returnRefAndGenerateDataTracker(_ref);
        }}
        className={cn('cursor-pointer', classNames?.target)}
      >
        {target}
      </RadixPopover.Trigger>

      <RadixPopover.Content
        {...contentProps}
        side={position}
        // prevents the infamous blue outline on safari
        style={{ outline: 'none' }}
        className={cn(
          !dynamicWidth && 'w-[var(--radix-popover-trigger-width)]',
          contentProps?.className,
          classNames?.content
        )}
      >
        <div
          className={cn(
            'bg-base-white rounded-xl border border-gray-300 p-6',
            classNames?.childrenWrapper
          )}
        >
          {showArrow && (
            <RadixPopover.Arrow
              className={cn('fill-gray-300', classNames?.arrow)}
            />
          )}
          {children}
        </div>
      </RadixPopover.Content>
    </RadixPopover.Root>
  );
}

export default Popover;
